import { useEffect, useState } from "react";
import api from "../lib/api";
import { Share, Copy, ArrowsClockwise, CalendarBlank, CheckCircle, UserPlus } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ShareCard() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState("");
  const [copied, setCopied] = useState(false);
  const [copiedJoin, setCopiedJoin] = useState(false);

  const load = async () => {
    const { data } = await api.get("/class/info");
    setInfo(data);
    if (data.exhibition_date) {
      // Convert ISO to local datetime-local string (YYYY-MM-DDTHH:mm)
      const d = new Date(data.exhibition_date);
      if (!isNaN(d)) {
        const pad = (n) => String(n).padStart(2, "0");
        setDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
      }
    }
  };

  useEffect(() => { load(); }, []);

  if (!info) return null;

  const parentLink = `${window.location.origin}/parents/${info.class_code}`;
  const joinLink = `${window.location.origin}/join/${info.class_code}`;

  const copyTo = async (text, setter, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      toast.success(`${label} copied`);
      setTimeout(() => setter(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Regenerate class code? The old links will stop working.")) return;
    setBusy(true);
    try {
      const { data } = await api.put("/class/info", { regenerate_code: true });
      setInfo(data);
      toast.success("New code generated");
    } finally { setBusy(false); }
  };

  const saveDate = async () => {
    setBusy(true);
    try {
      const iso = date ? new Date(date).toISOString() : "";
      const { data } = await api.put("/class/info", { exhibition_date: iso });
      setInfo(data);
      toast.success("Exhibition date saved");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm" data-testid="share-card">
      <div className="flex items-center gap-2 mb-3">
        <Share weight="fill" className="text-blue-500" size={20} />
        <h3 className="font-outfit font-bold text-xl">Share & invite</h3>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
          Class code
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="font-outfit font-black text-xl tracking-wide text-slate-900" data-testid="share-code">
            {info.class_code}
          </span>
          <button
            onClick={regenerate}
            disabled={busy}
            data-testid="share-regenerate-btn"
            className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-slate-100 text-slate-600"
          >
            <ArrowsClockwise size={14} /> Regenerate
          </button>
        </div>
      </div>

      {/* Student join link */}
      <div className="mb-4">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
          <UserPlus size={13} weight="bold" /> Student invite link
        </div>
        <p className="text-xs text-slate-600 mb-2">
          Share with new students. They enter their name and get an instant account.
        </p>
        <div className="flex gap-2">
          <input
            readOnly
            value={joinLink}
            data-testid="share-join-link-input"
            className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs text-slate-700 font-mono"
          />
          <button
            onClick={() => copyTo(joinLink, setCopiedJoin, "Join link")}
            data-testid="share-join-copy-btn"
            className="btn-tactile-white px-3 py-2 rounded-xl bg-white border border-slate-200 text-sm font-semibold hover:bg-slate-50 inline-flex items-center gap-1"
          >
            {copiedJoin ? <CheckCircle size={16} weight="fill" className="text-emerald-500" /> : <Copy size={16} weight="bold" />}
            {copiedJoin ? "Copied" : "Copy"}
          </button>
        </div>
      </div>

      {/* Parent portal link */}
      <div>
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
          <Share size={13} weight="bold" /> Parent portal link
        </div>
        <p className="text-xs text-slate-600 mb-2">
          Read-only page for parents — no student names, no progress.
        </p>
        <div className="flex gap-2">
          <input
            readOnly
            value={parentLink}
            data-testid="share-link-input"
            className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs text-slate-700 font-mono"
          />
          <button
            onClick={() => copyTo(parentLink, setCopied, "Parent link")}
            data-testid="share-copy-btn"
            className="btn-tactile-white px-3 py-2 rounded-xl bg-white border border-slate-200 text-sm font-semibold hover:bg-slate-50 inline-flex items-center gap-1"
          >
            {copied ? <CheckCircle size={16} weight="fill" className="text-emerald-500" /> : <Copy size={16} weight="bold" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>

      <div className="pt-4 mt-4 border-t border-slate-100">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1">
          <CalendarBlank size={14} /> Exhibition day
        </div>
        <div className="flex gap-2">
          <input
            type="datetime-local"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="share-date-input"
            className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
          />
          <button
            onClick={saveDate}
            disabled={busy}
            data-testid="share-save-date-btn"
            className="btn-tactile-yellow px-3 py-2 rounded-xl bg-yellow-400 text-yellow-950 text-sm font-bold hover:bg-yellow-500"
          >
            Save
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Drives the public countdown. Leave blank to hide it.
        </p>
      </div>
    </div>
  );
}
